import { createAction } from 'redux-actions'
import fetch from 'lib/fetch'
import formatRequestKey from 'lib/utils/format-request-key'
import { getSourceSettingsRequest, getSourceTrackEventsRequest } from 'selectors'

export const REQUEST_SOURCES = 'REQUEST_SOURCES'
export const REQUEST_SOURCES_ERROR = 'REQUEST_SOURCES_ERROR'
export const RECEIVE_SOURCES = 'RECEIVE_SOURCES'

export const fetchSources = workspaceSlug => ({
  types: {
    request: REQUEST_SOURCES,
    failure: REQUEST_SOURCES_ERROR,
    receive: RECEIVE_SOURCES
  },
  payload: { workspaceSlug },
  shouldCallAPI: state => {
    const { isRequesting, hasRequested, didRequestFail } = getSourceSettingsRequest(state, workspaceSlug)
    if (isRequesting) {
      return false
    }

    if (!hasRequested) {
      return true
    }

    return didRequestFail
  },
  callAPI: () => fetch(`/api/workspaces/${workspaceSlug}/projects`).then(res => {
    if (!res.ok) {
      const error = new Error('Unable to fetch projects')
      error.code = res.status
      throw error
    }
    return res.json()
  })
})

export const CREATING_SOURCE_SETTING = 'CREATING_SOURCE_SETTING'
export const CREATED_SOURCE_SETTING = 'CREATED_SOURCE_SETTING'
export const CREATE_SOURCE_SETTING_ERROR = 'CREATE_SOURCE_SETTING_ERROR'
export const createSourceSetting = (workspaceSlug, attributes) => ({
  types: {
    request: CREATING_SOURCE_SETTING,
    receive: CREATED_SOURCE_SETTING,
    failure: CREATE_SOURCE_SETTING_ERROR
  },
  payload: { workspaceSlug },
  callAPI () {
    return fetch(`/api/workspaces/${workspaceSlug}/projects`, {
      method: 'POST',
      body: JSON.stringify(attributes)
    })
      .then(res => {
        if (!res.ok) {
          const error = new Error('Unable to create source')
          error.code = res.status
          throw error
        }
        return res.json()
      })
  }
})

export const UPDATE_SOURCE = 'UPDATE_SOURCE'
export const UPDATED_SOURCE = 'UPDATED_SOURCE'
export const UPDATE_SOURCE_ERROR = 'UPDATE_SOURCE_ERROR'

export const updateSource = (workspaceSlug, sourceSlug, query) => ({
  types: {
    request: UPDATE_SOURCE,
    receive: UPDATED_SOURCE,
    failure: UPDATE_SOURCE_ERROR
  },
  payload: {
    workspaceSlug,
    sourceSlug,
    requestKey: formatRequestKey('updatesource', workspaceSlug, sourceSlug)
  },
  callAPI: () => fetch(`/api/workspaces/${workspaceSlug}/projects/${sourceSlug}`, {
    method: 'PATCH',
    body: JSON.stringify(query)
  }).then((res) => {
    if (!res.ok) {
      const error = new Error('Unable to update source')
      error.code = res.status
      throw error
    }
    return res.json()
  })
})

export const TOGGLE_SOURCE_INTEGRATION_SETTING_MAP_ACCORDION = 'TOGGLE_SOURCE_INTEGRATION_SETTING_MAP_ACCORDION'
export const toggleSourceIntegrationSettingMapAccordion = createAction(TOGGLE_SOURCE_INTEGRATION_SETTING_MAP_ACCORDION)
export const RENDER_SOURCE_INTEGRATION_SETTING_MAP_ACCORDION = 'RENDER_SOURCE_INTEGRATION_SETTING_MAP_ACCORDION'
export const renderSourceIntegrationSettingMapAccordion = createAction(RENDER_SOURCE_INTEGRATION_SETTING_MAP_ACCORDION)
export const CLEAR_SOURCE_INTEGRATION_SETTING_MAP_ACCORDIONS = 'CLEAR_SOURCE_INTEGRATION_SETTING_MAP_ACCORDIONS'
export const clearSourceIntegrationSettingMapAccordions = createAction(CLEAR_SOURCE_INTEGRATION_SETTING_MAP_ACCORDIONS)

export const REQUEST_SOURCE_TRACK_EVENTS = 'REQUEST_SOURCE_TRACK_EVENTS'
export const REQUEST_SOURCE_TRACK_EVENTS_ERROR = 'REQUEST_SOURCE_TRACK_EVENTS_ERROR'
export const RECEIVE_SOURCE_TRACK_EVENTS = 'RECEIVE_SOURCE_TRACK_EVENTS'

export const fetchSourceTrackEvents = ({ workspaceSlug, sourceSlug }) => ({
  types: {
    receive: RECEIVE_SOURCE_TRACK_EVENTS,
    failure: REQUEST_SOURCE_TRACK_EVENTS_ERROR,
    request: REQUEST_SOURCE_TRACK_EVENTS
  },
  payload: { workspaceSlug, sourceSlug },
  shouldCallAPI: state => {
    const { isRequesting, hasRequested, didRequestFail } = getSourceTrackEventsRequest(state, workspaceSlug, sourceSlug)
    if (isRequesting) {
      return false
    }

    if (!hasRequested) {
      return true
    }

    return didRequestFail
  },
  callAPI: () => fetch(`/api/workspaces/${workspaceSlug}/projects/${sourceSlug}/schema/tracks`)
    .then(res => {
      if (!res.ok) {
        const error = new Error(`Unable to fetch track events for ${workspaceSlug}/${sourceSlug}`)
        error.status = res.status
        throw error
      }
      return res.json()
    })
})

export const RESET_CHECK_SOURCE_SLUG = 'RESET_CHECK_SOURCE_SLUG'
export const REQUEST_CHECK_SOURCE_SLUG = 'REQUEST_CHECK_SOURCE_SLUG'
export const REQUEST_CHECK_SOURCE_SLUG_ERROR = 'REQUEST_CHECK_SOURCE_SLUG_ERROR'
export const RECEIVE_CHECK_SOURCE_SLUG = 'RECEIVE_CHECK_SOURCE_SLUG'

export const resetCheckSourceSlug = createAction(RESET_CHECK_SOURCE_SLUG)

export const checkSourceSlug = (workspaceSlug, sourceSlug) => ({
  types: {
    receive: RECEIVE_CHECK_SOURCE_SLUG,
    failure: REQUEST_CHECK_SOURCE_SLUG_ERROR,
    request: REQUEST_CHECK_SOURCE_SLUG
  },
  payload: { workspaceSlug, sourceSlug },
  callAPI: () => fetch(`/api/workspaces/${workspaceSlug}/projects?slug=${sourceSlug}`, {
    method: 'HEAD'
  }).then(res => {
    if (res.status >= 500) {
      const error = new Error('Unable to check source slug')
      error.state = res.status
      throw error
    }
    return res.status !== 404
  }),
  meta: {
    debounce: {
      time: 300,
      key: 'CHECK_SOURCE_SLUG'
    }
  }
})

export const REQUEST_SOURCE_BASE_DATA = 'REQUEST_SOURCE_BASE_DATA'
export const REQUEST_SOURCE_BASE_DATA_ERROR = 'REQUEST_SOURCE_BASE_DATA_ERROR'
export const RECEIVE_SOURCE_BASE_DATA = 'RECEIVE_SOURCE_BASE_DATA'

export const getSourceBaseData = (workspaceSlug, sourceMetadataSlug) => ({
  types: {
    receive: RECEIVE_SOURCE_BASE_DATA,
    failure: REQUEST_SOURCE_BASE_DATA_ERROR,
    request: REQUEST_SOURCE_BASE_DATA
  },
  payload: { workspaceSlug, sourceMetadataSlug },
  callAPI: () => fetch(`/api/workspaces/${workspaceSlug}/projects/base/${sourceMetadataSlug}`)
    .then(res => {
      if (!res.ok) {
        const error = new Error('Unable to fetch source base data')
        error.status = res.status
        throw error
      }
      return res.json()
    })
})

export const REQUEST_SOURCE_SETTING_INTEGRATIONS = 'REQUEST_SOURCE_SETTING_INTEGRATIONS'
export const REQUEST_SOURCE_SETTING_INTEGRATIONS_ERROR = 'REQUEST_SOURCE_SETTING_INTEGRATIONS_ERROR'
export const RECEIVE_SOURCE_SETTING_INTEGRATIONS = 'RECEIVE_SOURCE_SETTING_INTEGRATIONS'
export const fetchSourceSettingIntegrations = (workspaceSlug, sourceSettingSlug) => ({
  types: {
    receive: RECEIVE_SOURCE_SETTING_INTEGRATIONS,
    failure: REQUEST_SOURCE_SETTING_INTEGRATIONS_ERROR,
    request: REQUEST_SOURCE_SETTING_INTEGRATIONS
  },
  payload: { workspaceSlug, sourceSettingSlug },
  callAPI () {
    return fetch(`/api/workspaces/${workspaceSlug}/projects/${sourceSettingSlug}/integration-settings`)
      .then(res => {
        if (!res.ok) {
          const error = new Error('Unable to fetch integrations')
          error.status = res.status
          throw error
        }
        return res.json()
      })
  }
})

export const REQUEST_SOURCE_STATS = 'REQUEST_SOURCE_STATS'
export const REQUEST_SOURCE_STATS_ERROR = 'REQUEST_SOURCE_STATS_ERROR'
export const RECEIVE_SOURCE_STATS = 'RECEIVE_SOURCE_STATS'
export const fetchSourceStats = (workspaceSlug, date) => ({
  types: {
    receive: RECEIVE_SOURCE_STATS,
    failure: REQUEST_SOURCE_STATS_ERROR,
    request: REQUEST_SOURCE_STATS
  },
  payload: { workspaceSlug },
  callAPI () {
    return fetch(`/api/workspaces/${workspaceSlug}/source-stats${date ? '?date=' + date : ''}`)
      .then(res => {
        if (!res.ok) {
          const error = new Error('Unable to fetch source stats')
          error.status = res.status
          throw error
        }
        return res.json()
      })
  }
})



// WEBPACK FOOTER //
// ./actions/sources.js